// Lodge & Co — Market Intelligence DEMO layer (single source of mock data).
// All values are fictional and clearly flagged "Démonstration" in the UI.
// Structured to later receive real ADI / Tourism / Macro / Lodge Intelligence data.
import { DEMO_META } from "@/lib/demoData";

export const MONTHS_12 = ["Juil", "Aoû", "Sep", "Oct", "Nov", "Déc", "Jan", "Fév", "Mar", "Avr", "Mai", "Juin"];
// Fallback only — the live selector is populated dynamically from GET /api/market-data/markets.
// Kept minimal (FR-WORLD default) so no non-'ready' phantom market ever shows before the fetch.
export const MARKET_OPTIONS = [
  { value: "FR-WORLD", label: "France → Monde", origin: "France", destination: "Monde" },
];

export const MARKET_INTELLIGENCE_DEMO = {
  // Explicit synthetic-demo provenance (B3). Consumed by ProvenanceBadges via MI.meta.
  meta: DEMO_META,
  market: MARKET_OPTIONS[0],
  period: { label: "12 derniers mois disponibles", months: MONTHS_12 },

  kpis: {
    airPassengers: { key: "airPassengers", label: "Passagers aériens", value: "90,8", unit: "M", sub: "Total 12 derniers mois", delta: "+7,2 %", positive: true },
    airRevenue: { key: "airRevenue", label: "Revenus aériens", value: "19,4", unit: "Md $", sub: "Base Revenue", delta: "+11,3 %", positive: true },
    averageFare: { key: "averageFare", label: "Tarif moyen", value: "213", unit: "$", sub: "Par passager", delta: "+3,8 %", positive: true },
    internationalVisitors: { key: "internationalVisitors", label: "Visiteurs internationaux", value: null, unit: null, sub: "Données Offices du Tourisme", delta: null, pending: true },
  },

  topAirlinesByPassengers: [
    { rank: 1, name: "easyJet", value: 14.2, share: 15.6 },
    { rank: 2, name: "Transavia France", value: 11.8, share: 13.0 },
    { rank: 3, name: "Air France", value: 10.9, share: 12.0 },
    { rank: 4, name: "Ryanair", value: 9.4, share: 10.3 },
    { rank: 5, name: "Volotea", value: 6.1, share: 6.7 },
  ],
  topAirlinesByRevenue: [
    { rank: 1, name: "Air France", value: 4.8, share: 24.7 },
    { rank: 2, name: "easyJet", value: 2.6, share: 13.4 },
    { rank: 3, name: "Transavia France", value: 2.1, share: 10.8 },
    { rank: 4, name: "Emirates", value: 1.7, share: 8.8 },
    { rank: 5, name: "Delta Air Lines", value: 1.3, share: 6.7 },
  ],

  // Monthly evolution — passengers (M) vs international visitors (M)
  monthly: [
    { month: "Juil", passengers: 8.9, visitors: 6.1 },
    { month: "Aoû", passengers: 9.4, visitors: 6.8 },
    { month: "Sep", passengers: 8.1, visitors: 5.4 },
    { month: "Oct", passengers: 7.2, visitors: 4.6 },
    { month: "Nov", passengers: 6.4, visitors: 3.9 },
    { month: "Déc", passengers: 7.0, visitors: 4.3 },
    { month: "Jan", passengers: 6.1, visitors: 3.5 },
    { month: "Fév", passengers: 6.3, visitors: 3.7 },
    { month: "Mar", passengers: 7.1, visitors: 4.4 },
    { month: "Avr", passengers: 7.8, visitors: 5.2 },
    { month: "Mai", passengers: 8.3, visitors: 5.8 },
    { month: "Juin", passengers: 8.2, visitors: 5.9 },
  ],

  visitorSeasonality: [
    { month: "J", value: 42 }, { month: "F", value: 45 }, { month: "M", value: 54 },
    { month: "A", value: 63 }, { month: "M", value: 71 }, { month: "J", value: 86 },
    { month: "J", value: 100 }, { month: "A", value: 97 }, { month: "S", value: 68 },
    { month: "O", value: 57 }, { month: "N", value: 46 }, { month: "D", value: 60 },
  ],

  visitorOrigins: [
    { name: "Royaume-Uni", value: 18.4 },
    { name: "Allemagne", value: 15.1 },
    { name: "États-Unis", value: 12.7 },
    { name: "Belgique", value: 9.3 },
    { name: "Espagne", value: 7.8 },
    { name: "Autres", value: 36.7 },
  ],

  economicContext: [
    { label: "PIB", value: "3 030 Md $", trend: [96, 98, 99, 101, 103, 104], delta: "+1,4 %", positive: true },
    { label: "Croissance PIB", value: "+1,1 %", trend: [0.6, 0.8, 0.9, 1.0, 1.1, 1.1], delta: "stable", positive: true },
    { label: "Population", value: "68,2 M", trend: [67.4, 67.6, 67.8, 67.9, 68.1, 68.2], delta: "+0,3 %", positive: true },
    { label: "Taux de change", value: "1 € = 1,08 $", trend: [1.05, 1.06, 1.07, 1.08, 1.09, 1.08], delta: "-0,4 %", positive: false },
  ],

  dataSources: [
    { name: "Lodge Data", desc: "Données aériennes", status: "À venir" },
    { name: "Offices du Tourisme", desc: "Visiteurs internationaux", status: "À venir" },
    { name: "Eurostat", desc: "Données européennes", status: "À venir" },
    { name: "World Bank", desc: "Macro-économie", status: "À venir" },
    { name: "Actualités sectorielles", desc: "Veille marché", status: "À venir" },
  ],

  intelligenceSignals: [
    { id: "high-value", title: "Marché à forte valeur", category: "High Value Market", priority: "Élevé",
      insight: "Les États-Unis génèrent 12 % du volume mais 21 % des revenus aériens — valeur par passager supérieure à la moyenne.",
      metrics: [{ label: "Part revenu", value: "21 %" }, { label: "Part volume", value: "12 %" }], cta: "Explorer" },
    { id: "seasonal", title: "Fenêtre saisonnière", category: "Seasonal Opportunity", priority: "Moyen",
      insight: "La demande visiteurs culmine en juillet-août : opportunité de captation sur l'épaule de saison (juin & septembre).",
      metrics: [{ label: "Pic", value: "Juil." }, { label: "Potentiel", value: "+18 %" }], cta: "Explorer" },
    { id: "airline", title: "Partenariat compagnie", category: "Airline Partnership", priority: "Élevé",
      insight: "3 compagnies concentrent 40 % des sièges — un partenariat ciblé maximiserait la visibilité destination.",
      metrics: [{ label: "Concentration", value: "40 %" }, { label: "Compagnies", value: "3" }], cta: "Explorer" },
    { id: "growth", title: "Signal de croissance", category: "Growth Signal", priority: "Moyen",
      insight: "Trafic aérien en hausse de +7,2 % sur 12 mois, tiré par le segment loisirs long-courrier.",
      metrics: [{ label: "Croissance", value: "+7,2 %" }, { label: "Segment", value: "Loisirs" }], cta: "Explorer" },
    { id: "marketing", title: "Fenêtre marketing", category: "Marketing Window", priority: "Faible",
      insight: "Avril-mai présente un coût d'acquisition plus bas et une intention de voyage en progression — idéal pour une campagne.",
      metrics: [{ label: "Période", value: "Avr.–Mai" }, { label: "Intention", value: "+12 %" }], cta: "Explorer" },
  ],

  // News must always be sourced, dated and linked. There is deliberately no
  // illustrative fallback: the dashboard reads published server snapshots.
  marketNews: [],
};
